// @flow

import React, { Component, useRef } from 'react';

import { useDrag, useDrop } from 'react-dnd';

import StyleSheet from 'stilr';
import { Cell, palm } from 'react-flexr';

import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconMenu from 'app/common/IconMenu';

import i18n from 'app/common/i18n';

export const styles = StyleSheet.create({
  container: {
    maxWidth: '400px',
    height: '175px',
    marginBottom: '22px',
    overflow: 'visible',
    position: 'relative',
    textAlign: 'center',

    [palm]: {
      width: '100%',
      height: '160px'
    }
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    cursor: 'move'
  },
  progress: {
    position: 'absolute !important',
    top: '50%',
    left: '50%',
    margin: '-40px 0 0 -40px'
  },
  imageOptions: {
    position: 'absolute',
    top: '-1em',
    right: '-0.5em',
    zIndex: 1
  },
  imageOptionsBtn: {
    backgroundColor: '#ffffff',
    borderRadius: '1em',
    boxShadow: '0 0 3px rgba(0,0,0,0.75)'
  }
});

type Props = {
  admin: boolean,
  file: {},
  onChange: () => void,
  isDragging: boolean,
  dragDropRef: (node: ?HTMLElement) => void
};

class Image extends Component<Props> {
  handleDelete = () => {
    const confirmed = window.confirm(
      this.props.i18n('Delete image permanently?')
    );
    if (confirmed) {
      this.props.onChange(this.props.file, { type: 'delete', confirmed });
    }
  };

  render() {
    const { file, isDragging, dragDropRef, admin } = this.props;

    const status = file.get('status');
    const src = file.get('preview')
      ? file.get('preview')
      : file.get('path')
      ? `/${file.get('path')}/thumb`
      : null;

    const isUploading = status === 'uploading';
    const isFailed = status === 'failed';

    const opacity = isDragging ? 0.25 : 1;
    const imageOpacity = !isDragging && (isUploading || isFailed) ? 0.5 : 1;

    const image = src ? (
      <div
        ref={dragDropRef}
        style={{
          backgroundImage: `url('${src}')`,
          opacity: imageOpacity
        }}
        className={styles.image}
      />
    ) : null;

    return (
      <Cell
        size="1/3"
        lap="1/2"
        palm="1/1"
        className={styles.container}
        style={{ opacity }}
      >
        {image}
        {file.get('path') && (
          <div className={styles.imageOptions}>
            {admin ? (
              <IconMenu
                icon={<Icon className={styles.imageOptionsBtn}>more_vert</Icon>}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              >
                <MenuItem
                  component="a"
                  href={`/${file.get('path')}`}
                  target="_blank"
                >
                  <ListItemIcon>
                    <Icon>cloud_download</Icon>
                  </ListItemIcon>
                  <ListItemText primary={this.props.i18n('Download')} />
                </MenuItem>
                <MenuItem
                  component="a"
                  href={`/${file.get('path')}/raw`}
                  target="_blank"
                >
                  <ListItemIcon>
                    <Icon>cloud_download</Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary={this.props.i18n('Download raw file')}
                  />
                </MenuItem>
                <MenuItem onClick={this.handleDelete}>
                  <ListItemIcon>
                    <Icon>delete_forever</Icon>
                  </ListItemIcon>
                  <ListItemText primary={this.props.i18n('Delete')} />
                </MenuItem>
              </IconMenu>
            ) : (
              <IconButton aria-label="Delete" onClick={this.handleDelete}>
                <Icon className={styles.imageOptionsBtn}>clear</Icon>
              </IconButton>
            )}
          </div>
        )}
        {isFailed ? (
          <Icon
            className={`${styles.progress}`}
            style={{ color: 'darkRed', fontSize: '80px' }}
          >
            error
          </Icon>
        ) : isUploading ? (
          <CircularProgress
            size={80}
            thickness={8}
            className={styles.progress}
          />
        ) : null}
      </Cell>
    );
  }
}

const ItemTypes = {
  CARD: 'card'
};

function ImageDndWrapper(props) {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.CARD,
    item: {
      index: props.index
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }));

  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.CARD,
      hover(item, monitor) {
        const dragIndex = item.index;
        const hoverIndex = props.index;

        if (dragIndex === hoverIndex) {
          return;
        }

        if (!ref.current) {
          return;
        }

        const distance = Math.abs(dragIndex - hoverIndex);

        if (distance < 2) {
          const hoverBoundingRect = ref.current.getBoundingClientRect();
          const hoverWidth = hoverBoundingRect.right - hoverBoundingRect.left;
          const hoverOneThird = hoverWidth / 3;
          const clientOffset = monitor.getClientOffset();

          if (!clientOffset) {
            return;
          }

          const hoverClientX = clientOffset.x - hoverBoundingRect.left;

          if (dragIndex < hoverIndex && hoverClientX < hoverOneThird) {
            return;
          }

          if (dragIndex > hoverIndex && hoverClientX > hoverOneThird * 2) {
            return;
          }
        }

        props.onChange(null, { type: 'move', dragIndex, hoverIndex });
        item.index = hoverIndex;
      }
    }),
    [props.index, props.onChange]
  );

  const dragDropRef = (node) => {
    ref.current = node;
    drag(drop(node));
  };

  return <Image {...props} isDragging={isDragging} dragDropRef={dragDropRef} />;
}

export default i18n(ImageDndWrapper);
